import React, { useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
} from "@ionic/react";
import { useIonRouter } from "@ionic/react";
import { useLoginWithCode, useSendLoginCode } from "@/hooks";
import { getErrorMessage } from "@/api/client";
import { registrationStorage } from "@/services/storage";
import TwoFactorChallenge from "../TwoFactorChallenge";
import "./Auth.css";
import { Did } from "@/types";
import AppLogo from "@components/AppLogo.tsx";

// Default code configuration (will be updated from API response)
const DEFAULT_CODE_CHARACTERS = "BCDFGHJKLMNPQRSTVWXYZ";
const DEFAULT_CODE_LENGTH = 6;

const EnterCode: React.FC = () => {
  const router = useIonRouter();
  const [email, setEmail] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [codeCharacters, setCodeCharacters] = useState<string>(
    DEFAULT_CODE_CHARACTERS
  );
  const [codeLength, setCodeLength] = useState<number>(DEFAULT_CODE_LENGTH);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [userId, setUserId] = useState<Did | null>(null);
  const loginWithCodeMutation = useLoginWithCode();
  const resendMutation = useSendLoginCode();

  useEffect(() => {
    // Load email from storage
    registrationStorage.getEmail().then((storedEmail) => {
      if (storedEmail) {
        setEmail(storedEmail);
      } else {
        // No email stored, go back to welcome
        router.push("/auth/welcome", "back", "replace");
      }
    });
  }, [router]);

  const handleCodeChange = (value: string | null | undefined) => {
    const regex = new RegExp(`[^${codeCharacters}]`, "gi");
    const cleaned = (value || "")
      .toUpperCase()
      .replace(regex, "")
      .slice(0, codeLength);
    setCode(cleaned);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== codeLength || !email) return;

    try {
      const result = await loginWithCodeMutation.mutateAsync({ email, code });

      // Check if 2FA is required
      if (result.two_factor_required && result.user_id) {
        setTwoFactorRequired(true);
        setUserId(result.user_id);
      }
      // Navigation for successful login handled by hook
    } catch {
      // Error handled by mutation
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      const response = await resendMutation.mutateAsync(email);
      // Update code configuration from response
      if (response.code_characters) {
        setCodeCharacters(response.code_characters);
      }
      if (response.code_length) {
        setCodeLength(response.code_length);
      }
    } catch {
      // Error handled by mutation
    }
  };

  const handlePasswordLogin = () => {
    router.push("/auth/enter-password", "forward");
  };

  const handleBack = () => {
    registrationStorage.clearEmail();
    router.push("/auth/welcome", "back");
  };

  const handleBackFrom2FA = () => {
    setTwoFactorRequired(false);
    setUserId(null);
  };

  // If 2FA is required, show the TwoFactorChallenge component
  if (twoFactorRequired && userId) {
    return <TwoFactorChallenge userId={userId} onBack={handleBackFrom2FA} />;
  }

  return (
    <IonPage>
      <IonContent className="auth-content ion-padding">
        <div className="auth-container">
          <div className="auth-header">
            <AppLogo />
            <IonText color="dark">
              <h1>Inloggen met code</h1>
              <p>
                We hebben een inlogcode gestuurd naar
                <br />
                <strong>{email}</strong>
              </p>
            </IonText>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <IonInput
              className="code-input"
              style={{
                width: "100%",
                maxWidth: "200px",
                margin: "0 auto 24px",
                display: "block",
                textTransform: "uppercase",
              }}
              type="text"
              placeholder={"X".repeat(codeLength)}
              value={code}
              maxlength={codeLength}
              onIonInput={(e) => handleCodeChange(e.detail.value)}
            />

            {loginWithCodeMutation.error && (
              <IonText color="danger" className="error-text">
                <p>{getErrorMessage(loginWithCodeMutation.error)}</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              type="submit"
              className="auth-button"
              disabled={
                code.length !== codeLength || loginWithCodeMutation.isPending
              }
            >
              {loginWithCodeMutation.isPending ? (
                <>
                  <IonSpinner name="crescent" />
                  <span style={{ marginLeft: "8px" }}>Inloggen...</span>
                </>
              ) : (
                "Inloggen"
              )}
            </IonButton>

            <div className="auth-link">
              <IonButton
                fill="clear"
                size="small"
                onClick={handleResend}
                disabled={resendMutation.isPending}
              >
                {resendMutation.isPending
                  ? "Code versturen..."
                  : "Code opnieuw versturen"}
              </IonButton>
            </div>

            {resendMutation.isSuccess && (
              <IonText color="success" className="error-text">
                <p style={{ textAlign: "center" }}>Nieuwe code verstuurd!</p>
              </IonText>
            )}

            <div className="auth-link">
              <IonButton
                fill="clear"
                size="small"
                onClick={handlePasswordLogin}
              >
                Inloggen met wachtwoord
              </IonButton>
            </div>

            <div className="auth-link">
              <IonButton fill="clear" size="small" onClick={handleBack}>
                Terug
              </IonButton>
            </div>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EnterCode;
